/**
 * Where each person got to in each lesson — remembered in this browser.
 *
 * Per signed-in user, so two people on one machine do not see each other's
 * ticks. Held in `localStorage`, which is a convenience and not a record: it
 * never leaves the browser, does not follow a person to another device, and can
 * be empty, blocked or full, so every access is guarded and the page is correct
 * without it. A lesson is marked watched once 90% has played, which is what the
 * brief for this module asked for and is forgiving of a person who stops when
 * the recap starts.
 */

const STORAGE_KEY = 'tmverse.courses.progress'

/** The fraction of a lesson that must have played to count as watched. */
export const WATCHED_AT = 0.9

export interface LessonProgress {
  /** Seconds; where to resume. */
  position: number
  watched: boolean
}

type Store = Record<string, Record<string, LessonProgress>>

function readStore(): Store {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Store)
      : {}
  } catch {
    return {}
  }
}

function valid(value: unknown): value is LessonProgress {
  if (typeof value !== 'object' || value === null) return false
  const entry = value as Record<string, unknown>
  return (
    typeof entry.position === 'number' &&
    Number.isFinite(entry.position) &&
    entry.position >= 0 &&
    typeof entry.watched === 'boolean'
  )
}

export function readProgress(userKey: string): Record<string, LessonProgress> {
  const mine = readStore()[userKey]
  if (!mine || typeof mine !== 'object') return {}
  return Object.fromEntries(Object.entries(mine).filter(([, entry]) => valid(entry)))
}

/**
 * Record a position. Watched is sticky: replaying the first minute of a lesson
 * already finished does not un-tick it.
 */
export function saveProgress(
  userKey: string,
  code: string,
  position: number,
  duration: number,
): LessonProgress {
  const store = readStore()
  const mine = store[userKey] ?? {}
  const before = valid(mine[code]) ? mine[code] : undefined
  const reached = duration > 0 && position / duration >= WATCHED_AT
  const next: LessonProgress = {
    position: Math.max(0, Math.floor(position)),
    watched: Boolean(before?.watched) || reached,
  }
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...store, [userKey]: { ...mine, [code]: next } }),
    )
  } catch {
    // Private window, blocked storage or a full quota: nothing remembered.
  }
  return next
}
